import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Loader2, Play, RotateCcw, Eye, SkipForward, Disc3 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ROUNDS = 10;
const CLIP = 15;
const BIG = "h-16 px-7 rounded-2xl font-patient font-black text-xl inline-flex items-center gap-2 transition-colors active:scale-95";

export const GROUP_BLINDTEST = { id: "music_blindtest", type: "music_blindtest", title: "Blind test musical", description: "" };

function ScoringChoice({ onChoose }) {
  return (
    <div className="rounded-3xl bg-white border-4 border-rose-100 p-8 text-center" data-testid="gbt-scoring-choice">
      <Disc3 className="w-14 h-14 text-rose-500 mx-auto mb-3" />
      <p className="font-patient font-black text-3xl text-gray-800 mb-2">On compte les points ?</p>
      <p className="text-gray-500 mb-6">Les résidents devinent à voix haute, puis vous révélez la réponse.</p>
      <div className="flex flex-wrap justify-center gap-4">
        <button onClick={() => onChoose(true)} data-testid="gbt-with-points" className={`${BIG} bg-emerald-700 hover:bg-emerald-800 text-white`}>Avec points</button>
        <button onClick={() => onChoose(false)} data-testid="gbt-no-points" className={`${BIG} bg-white border-4 border-gray-200 text-gray-700 hover:bg-gray-50`}>Pour le plaisir</button>
      </div>
    </div>
  );
}

// Blind test de groupe : extraits au hasard, l'animateur révèle la réponse.
export function GroupBlindTest({ onEnd, setScoring }) {
  const [tracks, setTracks] = useState(null);
  const [chosen, setChosen] = useState(false);
  const [n, setN] = useState(0);
  const [shown, setShown] = useState(false);
  const audio = useRef(null);
  const timer = useRef(null);
  useEffect(() => { api.get("/music").then((r) => setTracks(r.data)).catch(() => setTracks([])); }, []);
  useEffect(() => () => { clearTimeout(timer.current); audio.current?.pause(); }, []);
  const rounds = useMemo(() => (tracks ? [...tracks].sort(() => Math.random() - 0.5).slice(0, ROUNDS) : []), [tracks]);
  const t = rounds[n];

  const play = (full = false) => {
    const a = audio.current;
    if (!a) return;
    clearTimeout(timer.current);
    if (!full) a.currentTime = t.start || 0;
    a.play().catch(() => {});
    if (!full) timer.current = setTimeout(() => a.pause(), CLIP * 1000);
  };
  const reveal = () => { setShown(true); play(true); };
  const next = () => {
    audio.current?.pause(); clearTimeout(timer.current);
    if (n + 1 < rounds.length) { setN(n + 1); setShown(false); } else onEnd();
  };

  if (!tracks) return <Loader2 className="w-8 h-8 animate-spin text-emerald-700 mx-auto" />;
  if (rounds.length < 3) return <p className="text-xl font-patient text-gray-500 text-center py-10" data-testid="gbt-empty">Ajoutez au moins 3 musiques dans l'admin pour jouer.</p>;
  if (!chosen) return <ScoringChoice onChoose={(v) => { setScoring(v); setChosen(true); }} />;
  return (
    <div className="rounded-3xl bg-white border-4 border-rose-100 p-8 text-center" data-testid="gbt-round">
      <p className="font-patient text-xl text-gray-500 mb-4" data-testid="gbt-progress">Chanson {n + 1} sur {rounds.length}</p>
      <audio ref={audio} src={`${BACKEND}${t.audio_url}`} preload="auto" />
      {shown ? (
        <div className="mb-8 soft-in" data-testid="gbt-answer">
          <p className="font-patient font-black text-4xl sm:text-5xl text-gray-800">{t.title}</p>
          <p className="font-patient text-2xl text-gray-500 mt-2">{[t.artist, t.year].filter(Boolean).join(" · ")}</p>
        </div>
      ) : (
        <p className="font-patient font-black text-4xl text-rose-300 mb-8 tracking-widest" data-testid="gbt-hidden">? ? ?</p>
      )}
      <div className="flex flex-wrap justify-center gap-4">
        {!shown && (
          <>
            <button onClick={() => play()} data-testid="gbt-play" className={`${BIG} bg-rose-500 hover:bg-rose-600 text-white`}><Play className="w-6 h-6" /> Écouter l'extrait</button>
            <button onClick={() => play()} data-testid="gbt-replay" className={`${BIG} bg-white border-4 border-rose-200 text-rose-700 hover:bg-rose-50`}><RotateCcw className="w-6 h-6" /> Réécouter</button>
            <button onClick={reveal} data-testid="gbt-reveal" className={`${BIG} bg-amber-400 hover:bg-amber-300 text-amber-950`}><Eye className="w-6 h-6" /> Révéler la réponse</button>
          </>
        )}
        {shown && (
          <button onClick={next} data-testid="gbt-next" className={`${BIG} bg-emerald-700 hover:bg-emerald-800 text-white`}>
            <SkipForward className="w-6 h-6" /> {n + 1 < rounds.length ? "Chanson suivante" : "Terminer"}
          </button>
        )}
      </div>
    </div>
  );
}
